import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, Subject, catchError, map, of, throwError, tap } from "rxjs";
import { environment } from "src/environments/environment";
import { NavigationMenuSelection } from "../interfaces/navigation_menu.interface";
import { ProductLicensesService } from "./product_licenses.service";
import { FormResponseChangesService } from "./form_response_changes.service";
import { LoginService } from "./login.service";
import type { PersistedEvidenceRequirement } from "./evidence_requirements.service";

export interface QuestionResponseDto {
    section: string;
    target: string;
    selectedOption: number | null;
    complianceStatus?: 0 | 1 | null;
    qsaReviewed?: 0 | 1 | null;
    qsa_reviewed?: 0 | 1 | null;
    resultsDescription: string;
    responsePayload?: unknown;
    respondedAt: string | null;
    respondedByName: string | null;
}

interface ApiResponse<T> {
    success: boolean;
    data?: T;
    message?: string;
}

export interface QuestionResponsePayload {
    selectedOption: number | null;
    resultsDescription: string;
}

export type QuestionResponseSaveMode = "reviewed" | "draft";

export interface AppendixDependencyEntry {
    questionReference: string;
    sectionKey: string;
}

export interface AppendixDependencyMap {
    appendix_b: AppendixDependencyEntry[];
    appendix_c: AppendixDependencyEntry[];
    appendix_d: AppendixDependencyEntry[];
    appendix_e: AppendixDependencyEntry[];
}

export interface NeedsReviewResponse {
    complianceStatus: 0 | 1 | null;
    qsaReviewed?: 0 | 1 | null;
    qsa_reviewed?: 0 | 1 | null;
}

export interface ReducedScopeAutocompleteTextUpdateResponse {
    productLicenseId: number;
    updatedCount: number;
    outOfScopeUpdatedCount: number;
    inScopeResetCount: number;
    selectedSaqTypes: string[];
    questionReferences: string[];
}

export interface CsvImportAuditRequest {
    productLicenseId: number;
    section: string;
    requirement: string;
    tableKey: string;
    acceptedRowCount: number;
    sanitizedFilename: string;
}

export type QuestionResponseMutationActionMode = QuestionResponseSaveMode | "needs_review";

export interface QuestionResponseMutationSummary {
    totalRequirements: number;
    completedRequirements: number;
    pendingRequirements: number;
    completionPercent: number;
    compliantInScopeRequirements?: NavigationMenuSelection[];
}

export interface QuestionResponseMutationQsaReviewSummary {
    totalRequirements: number;
    reviewedRequirements: number;
    pendingRequirements: number;
    reviewPercent: number;
}

export interface QuestionResponseMutationCoverSummaryRequirement {
    section: string;
    target: string;
    complianceStatus: 0 | 1 | null;
    qsaReviewed: 0 | 1;
    selectedOption: number | null;
    respondedAt: string | null;
    respondedByName: string | null;
}

export interface QuestionResponseMutationCoverSummary {
    totalRequirements: number;
    completedRequirements: number;
    pendingRequirements: number;
    completionPercent: number;
    savedRequirement: QuestionResponseMutationCoverSummaryRequirement | null;
}

export interface QuestionResponseMutationCommentsWidget {
    total: number;
    latestCreatedAt: string | null;
    latestUpdatedAt: string | null;
}

export interface QuestionResponseMutationRecentActivityWidgetLatest {
    id: number | null;
    createdAt: string | null;
    action: string | null;
}

export interface QuestionResponseMutationRecentActivityWidget {
    total: number;
    latest: QuestionResponseMutationRecentActivityWidgetLatest | null;
}

export interface QuestionResponseMutationWidgets {
    comments?: QuestionResponseMutationCommentsWidget | null;
    recentActivity?: QuestionResponseMutationRecentActivityWidget | null;
    coverSummary?: QuestionResponseMutationCoverSummary | null;
}

export interface QuestionResponseMutationResponse {
    actionMode: QuestionResponseMutationActionMode;
    productLicenseId: number;
    selection: NavigationMenuSelection | null;
    mutation: unknown;
    savedResponse: QuestionResponseDto | null;
    complianceStatuses: NavigationMenuSelectionComplianceStatus[];
    evidenceRequirements?: PersistedEvidenceRequirement[] | null;
    complianceSummary: QuestionResponseMutationSummary | null;
    hasAssignedQa: boolean;
    qsaReviewSummary?: QuestionResponseMutationQsaReviewSummary;
    widgets?: QuestionResponseMutationWidgets | null;
}

export interface NavigationMenuSelectionComplianceStatus extends NavigationMenuSelection {
    complianceStatus: 0 | 1 | null;
    qsaReviewed?: 0 | 1;
    selectedOption?: number | null;
}

@Injectable({ providedIn: "root" })
export class QuestionResponsesService {
    private static readonly SECTION_PATTERN = /^[A-Za-z0-9_]+$/;
    private static readonly TARGET_PATTERN = /^[A-Za-z0-9 ._-]+$/;

    private readonly apiBaseUrl = environment.baseurl.replace(/\/$/, "");
    private activeProductLicenseId: number | null = null;
    private readonly mutationEventsSubject = new Subject<QuestionResponseMutationResponse>();
    private readonly complianceStatusRefreshSubject = new Subject<number | null>();

    constructor(
        private readonly http: HttpClient,
        private readonly productLicensesService: ProductLicensesService,
        private readonly formResponseChanges: FormResponseChangesService,
        private readonly loginService: LoginService
    ) {}

    private encodeTarget(value: string): string {
        try {
            return btoa(value);
        } catch (error) {
            return value;
        }
    }

    private decodeTarget(value: string | null | undefined): string {
        if (typeof value !== "string") {
            return "";
        }

        try {
            return atob(value);
        } catch (error) {
            return value;
        }
    }

    setActiveProductLicenseId(productLicenseId: number | null): void {
        if (productLicenseId === null || productLicenseId === undefined) {
            this.activeProductLicenseId = null;
            return;
        }

        const normalized = Number(productLicenseId);
        this.activeProductLicenseId = Number.isInteger(normalized) && normalized > 0 ? normalized : null;
    }

    getActiveProductLicenseId(): number | null {
        return this.activeProductLicenseId;
    }

    watchMutationEvents(): Observable<QuestionResponseMutationResponse> {
        return this.mutationEventsSubject.asObservable();
    }

    watchComplianceStatusRefresh(): Observable<number | null> {
        return this.complianceStatusRefreshSubject.asObservable();
    }

    requestComplianceStatusRefresh(productLicenseId?: number | null): void {
        this.complianceStatusRefreshSubject.next(this.resolveProductLicenseId(productLicenseId ?? undefined));
    }

    getQuestionResponse(
        selection: NavigationMenuSelection,
        productLicenseId?: number
    ): Observable<QuestionResponseDto | null> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before loading responses."));
        }
        const normalized = this.normalizeSelection(selection);
        if (!normalized) {
            return throwError(() => new Error("A valid section and target must be provided."));
        }
        return this.http
            .get<ApiResponse<QuestionResponseDto>>(`${this.apiBaseUrl}/question-responses`, {
                params: {
                    section: normalized.section,
                    target: this.encodeTarget(normalized.target),
                    productLicenseId: String(licenseId)
                }
            })
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        return null;
                    }

                    const decodedTarget = this.decodeTarget(response.data.target);
                    return {
                        ...response.data,
                        target: decodedTarget,
                        qsaReviewed: this.normalizeReviewStatus(
                            response.data.qsaReviewed ?? response.data.qsa_reviewed
                        )
                    };
                }),
                catchError((error) => {
                    if (error?.status === 404) {
                        return of(null);
                    }
                    return throwError(() => error);
                })
            );
    }

    saveQuestionResponse(
        selection: NavigationMenuSelection,
        payload: QuestionResponsePayload,
        saveModeOrProductLicenseId?: QuestionResponseSaveMode | number,
        productLicenseId?: number
    ): Observable<QuestionResponseDto> {
        const { saveMode, resolvedProductLicenseId } = this.resolveSaveParams(
            saveModeOrProductLicenseId,
            productLicenseId
        );
        const licenseId = this.resolveProductLicenseId(resolvedProductLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before saving responses."));
        }
        const normalized = this.normalizeSelection(selection);
        if (!normalized) {
            return throwError(() => new Error("A valid section and target must be provided."));
        }
        return this.http
            .post<ApiResponse<QuestionResponseDto>>(
                `${this.apiBaseUrl}/question-responses`,
                {
                    ...payload,
                    saveMode,
                    section: normalized.section,
                    target: this.encodeTarget(normalized.target),
                    productLicenseId: licenseId
                }
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Failed to save the response.");
                    }
                    return {
                        ...response.data,
                        target: this.decodeTarget(response.data.target),
                        qsaReviewed: this.normalizeReviewStatus(
                            response.data.qsaReviewed ?? response.data.qsa_reviewed
                        ),
                        respondedByName: this.resolveResponderDisplayName(response.data.respondedByName)
                    };
                }),
                tap(() => {
                    this.requestProductLicenseRefresh();
                    this.formResponseChanges.resetUserResponseChanged();
                })
            );
    }

    private resolveSaveParams(
        saveModeOrProductLicenseId?: QuestionResponseSaveMode | number,
        productLicenseId?: number
    ): { saveMode: QuestionResponseSaveMode; resolvedProductLicenseId?: number } {
        if (saveModeOrProductLicenseId === "draft" || saveModeOrProductLicenseId === "reviewed") {
            return { saveMode: saveModeOrProductLicenseId, resolvedProductLicenseId: productLicenseId };
        }

        if (typeof saveModeOrProductLicenseId === "number") {
            return { saveMode: "reviewed", resolvedProductLicenseId: saveModeOrProductLicenseId };
        }

        return { saveMode: "reviewed", resolvedProductLicenseId: productLicenseId };
    }

    getSelectionComplianceStatuses(
        productLicenseId?: number,
        options?: { forceRefresh?: boolean }
    ): Observable<NavigationMenuSelectionComplianceStatus[]> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return of([]);
        }

        const params: Record<string, string> = { productLicenseId: String(licenseId) };
        if (options?.forceRefresh) {
            params.cacheBust = String(Date.now());
        }

        return this.http
            .get<ApiResponse<NavigationMenuSelectionComplianceStatus[]>>(
                `${this.apiBaseUrl}/question-responses/status/compliance`,
                {
                    params
                }
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !Array.isArray(response.data)) {
                        return [];
                    }

                    const decodedTargets = response.data.map((entry) =>
                        entry ? { ...entry, target: this.decodeTarget(entry.target) } : entry
                    );

                    return this.normalizeSelectionStatuses(decodedTargets);
                }),
                catchError(() => of([]))
            );
    }

    getAppendixDependencies(productLicenseId?: number): Observable<AppendixDependencyMap> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return of(this.buildEmptyAppendixDependencyMap());
        }

        return this.http
            .get<ApiResponse<Partial<AppendixDependencyMap>>>(
                `${this.apiBaseUrl}/question-responses/appendix/dependencies`,
                {
                    params: { productLicenseId: String(licenseId) }
                }
            )
            .pipe(
                map((response) => this.normalizeAppendixDependencies(response?.data)),
                catchError(() => of(this.buildEmptyAppendixDependencyMap()))
            );
    }

    markQuestionResponseNeedsReview(
        selection: NavigationMenuSelection,
        productLicenseId?: number
    ): Observable<NeedsReviewResponse> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before updating responses."));
        }

        const normalized = this.normalizeSelection(selection);
        if (!normalized) {
            return throwError(() => new Error("A valid section and target must be provided."));
        }

        return this.http
            .post<ApiResponse<NeedsReviewResponse>>(
                `${this.apiBaseUrl}/question-responses/status/needs-review`,
                {
                    section: normalized.section,
                    target: this.encodeTarget(normalized.target),
                    productLicenseId: licenseId
                }
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Failed to update the response.");
                    }

                    return {
                        ...response.data,
                        qsaReviewed: this.normalizeReviewStatus(
                            response.data.qsaReviewed ?? response.data.qsa_reviewed
                        )
                    };
                }),
                tap(() => {
                    this.requestProductLicenseRefresh();
                })
            );
    }

    reapplyReducedScopeAutocompleteText(
        productLicenseId: number,
        autocompleteText: string,
        selectedSaqTypes: string[] = []
    ): Observable<ReducedScopeAutocompleteTextUpdateResponse> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before updating responses."));
        }

        return this.http
            .post<ApiResponse<ReducedScopeAutocompleteTextUpdateResponse>>(
                `${this.apiBaseUrl}/question-responses/reduced-scope/autocomplete-text`,
                {
                    productLicenseId: licenseId,
                    autocompleteText,
                    selectedSaqTypes
                }
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Failed to update reduced-scope responses.");
                    }

                    return {
                        productLicenseId: Number(response.data.productLicenseId) || licenseId,
                        updatedCount: Number(response.data.updatedCount) || 0,
                        outOfScopeUpdatedCount: Number(response.data.outOfScopeUpdatedCount) || 0,
                        inScopeResetCount: Number(response.data.inScopeResetCount) || 0,
                        selectedSaqTypes: Array.isArray(response.data.selectedSaqTypes)
                            ? response.data.selectedSaqTypes.filter((entry): entry is string => typeof entry === "string")
                            : [],
                        questionReferences: Array.isArray(response.data.questionReferences)
                            ? response.data.questionReferences.map((entry) => this.decodeTarget(entry))
                            : []
                    };
                }),
                tap(() => {
                    this.requestProductLicenseRefresh();
                    this.requestComplianceStatusRefresh(licenseId);
                })
            );
    }

    recordCsvImportAudit(payload: CsvImportAuditRequest): Observable<void> {
        const licenseId = this.resolveProductLicenseId(payload?.productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before recording Excel import activity."));
        }

        return this.http
            .post<ApiResponse<{ recorded: boolean }>>(`${this.apiBaseUrl}/question-responses/excel-import/audit`, {
                productLicenseId: licenseId,
                section: payload.section,
                target: this.encodeTarget(payload.requirement),
                tableKey: payload.tableKey,
                acceptedRowCount: payload.acceptedRowCount,
                sanitizedFilename: payload.sanitizedFilename
            })
            .pipe(
                map((response) => {
                    if (!response?.success) {
                        throw new Error(response?.message || "Failed to record Excel import activity.");
                    }
                })
            );
    }

    mutateQuestionResponse(
        selection: NavigationMenuSelection,
        payload: QuestionResponsePayload | null,
        actionMode: QuestionResponseMutationActionMode,
        productLicenseId?: number
    ): Observable<QuestionResponseMutationResponse> {
        const licenseId = this.resolveProductLicenseId(productLicenseId);
        if (licenseId === null) {
            return throwError(() => new Error("A product license must be selected before updating responses."));
        }

        const normalized = this.normalizeSelection(selection);
        if (!normalized) {
            return throwError(() => new Error("A valid section and target must be provided."));
        }

        return this.http
            .post<ApiResponse<QuestionResponseMutationResponse>>(
                `${this.apiBaseUrl}/question-responses/mutation`,
                {
                    actionMode,
                    saveMode: actionMode === "needs_review" ? "reviewed" : actionMode,
                    selectedOption: payload?.selectedOption ?? null,
                    resultsDescription: payload?.resultsDescription ?? "",
                    section: normalized.section,
                    target: this.encodeTarget(normalized.target),
                    productLicenseId: licenseId
                }
            )
            .pipe(
                map((response) => {
                    if (!response?.success || !response.data) {
                        throw new Error(response?.message || "Failed to update the response.");
                    }

                    const data = response.data;
                    const selectionPayload =
                        data.selection && typeof data.selection === "object"
                            ? {
                                  section:
                                      typeof data.selection.section === "string"
                                          ? data.selection.section
                                          : normalized.section,
                                  target:
                                      typeof data.selection.target === "string"
                                          ? this.decodeTarget(data.selection.target)
                                          : normalized.target
                              }
                            : null;
                    const savedResponse =
                        data.savedResponse && typeof data.savedResponse === "object"
                            ? {
                                  ...data.savedResponse,
                                  target: this.decodeTarget(data.savedResponse.target),
                                  qsaReviewed: this.normalizeReviewStatus(
                                      data.savedResponse.qsaReviewed ?? data.savedResponse.qsa_reviewed
                                  ),
                                  respondedByName: this.resolveResponderDisplayName(data.savedResponse.respondedByName)
                              }
                            : null;
                    const decodedStatuses = Array.isArray(data.complianceStatuses)
                        ? data.complianceStatuses.map((entry) =>
                              entry ? { ...entry, target: this.decodeTarget(entry.target) } : entry
                          )
                        : [];
                    const qsaReviewSummary = this.normalizeQsaReviewSummary(data.qsaReviewSummary);

                    return {
                        ...data,
                        selection: selectionPayload,
                        savedResponse,
                        complianceStatuses: this.normalizeSelectionStatuses(decodedStatuses),
                        complianceSummary:
                            data.complianceSummary && typeof data.complianceSummary === "object"
                                ? data.complianceSummary
                                : null,
                        hasAssignedQa: data.hasAssignedQa === true,
                        ...(qsaReviewSummary ? { qsaReviewSummary } : {}),
                        widgets: this.normalizeMutationWidgets(data.widgets)
                    };
                }),
                tap(() => {
                    this.formResponseChanges.resetUserResponseChanged();
                }),
                tap((mutation) => {
                    this.mutationEventsSubject.next(mutation);
                })
            );
    }

    private resolveResponderDisplayName(apiName: string | null | undefined): string | null {
        const userDisplayName = this.loginService.getCurrentUserDisplayName();

        if (typeof userDisplayName === "string") {
            const trimmedUserName = userDisplayName.trim();
            if (trimmedUserName) {
                return trimmedUserName;
            }
        }

        if (typeof apiName === "string") {
            const trimmedApiName = apiName.trim();
            if (trimmedApiName) {
                return trimmedApiName;
            }
        }

        return null;
    }

    requestProductLicenseRefresh(): void {
        void this.productLicensesService.loadLicenses(true).subscribe({
            error: () => {}
        });
    }

    private resolveProductLicenseId(productLicenseId?: number): number | null {
        const candidate = productLicenseId ?? this.activeProductLicenseId;
        const normalized = Number(candidate);

        if (!Number.isInteger(normalized) || normalized <= 0) {
            return null;
        }

        return normalized;
    }

    private normalizeSelection(selection: NavigationMenuSelection | null | undefined): NavigationMenuSelection | null {
        if (!selection) {
            return null;
        }

        const section = typeof selection.section === "string" ? selection.section.trim() : "";
        const target = typeof selection.target === "string" ? selection.target.trim() : "";

        if (!section || !target) {
            return null;
        }

        if (!QuestionResponsesService.SECTION_PATTERN.test(section)) {
            return null;
        }

        if (!QuestionResponsesService.TARGET_PATTERN.test(target)) {
            return null;
        }

        return { section, target };
    }

    private normalizeSelectionStatuses(
        selections: (NavigationMenuSelectionComplianceStatus | null | undefined)[]
    ): NavigationMenuSelectionComplianceStatus[] {
        if (!Array.isArray(selections) || selections.length === 0) {
            return [];
        }

        const unique = new Map<string, NavigationMenuSelectionComplianceStatus>();

        for (const selection of selections) {
            const normalized = this.normalizeSelection(selection);
            if (!normalized) {
                continue;
            }

            const key = `${normalized.section}|${normalized.target}`;

            const rawStatus =
                selection && typeof selection === "object"
                    ? (selection as NavigationMenuSelectionComplianceStatus).complianceStatus
                    : null;

            const complianceStatus =
                rawStatus === 0 || rawStatus === 1
                    ? rawStatus
                    : rawStatus === "0"
                        ? 0
                        : rawStatus === "1"
                            ? 1
                            : null;
            const rawQsaReviewed =
                selection && typeof selection === "object"
                    ? (selection as QuestionResponseDto).qsaReviewed ??
                      (selection as QuestionResponseDto).qsa_reviewed
                    : null;
            const hasQsaReviewed = rawQsaReviewed !== null && rawQsaReviewed !== undefined;
            const qsaReviewed = hasQsaReviewed ? this.normalizeReviewStatus(rawQsaReviewed) : null;

            const existing = unique.get(key);
            if (existing) {
                // Mutation payloads can include duplicate keys; keep the latest explicit 0/1 status.
                const rawSelectedOption =
                    selection && typeof selection === "object"
                        ? (selection as NavigationMenuSelectionComplianceStatus).selectedOption
                        : null;
                const selectedOption = this.normalizeSelectedOption(rawSelectedOption);
                const resolvedSelectedOption = selectedOption ?? existing?.selectedOption ?? null;
                const resolvedComplianceStatus =
                    complianceStatus === 0 || complianceStatus === 1 ? complianceStatus : existing.complianceStatus;
                unique.set(key, {
                    ...normalized,
                    complianceStatus: resolvedComplianceStatus,
                    qsaReviewed: qsaReviewed === null ? existing.qsaReviewed ?? 0 : qsaReviewed,
                    selectedOption: resolvedSelectedOption
                });
            } else {
                const rawSelectedOption =
                    selection && typeof selection === "object"
                        ? (selection as NavigationMenuSelectionComplianceStatus).selectedOption
                        : null;
                unique.set(key, {
                    ...normalized,
                    complianceStatus,
                    qsaReviewed: qsaReviewed ?? 0,
                    selectedOption: this.normalizeSelectedOption(rawSelectedOption)
                });
            }
        }

        return Array.from(unique.values());
    }

    private normalizeSelectedOption(value: unknown): number | null {
        const parsed = Number(value);

        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }

    private normalizeReviewStatus(value: unknown): 0 | 1 {
        return value === 1 || value === "1" || value === true ? 1 : 0;
    }

    private normalizeQsaReviewSummary(value: unknown): QuestionResponseMutationQsaReviewSummary | null {
        if (!value || typeof value !== "object") {
            return null;
        }

        const candidate = value as Record<string, unknown>;
        const toCount = (raw: unknown): number => {
            const numeric = Number(raw);
            return Number.isFinite(numeric) && numeric > 0 ? Math.floor(numeric) : 0;
        };
        const totalRequirements = toCount(candidate.totalRequirements ?? candidate.total_requirements);
        const reviewedRequirements = Math.min(
            totalRequirements,
            toCount(candidate.reviewedRequirements ?? candidate.reviewed_requirements)
        );
        const pendingRequirements = Math.max(totalRequirements - reviewedRequirements, 0);

        return {
            totalRequirements,
            reviewedRequirements,
            pendingRequirements,
            reviewPercent: totalRequirements > 0
                ? Math.round((reviewedRequirements / totalRequirements) * 100)
                : 0
        };
    }

    private normalizeMutationWidgets(value: unknown): QuestionResponseMutationWidgets | null {
        if (!value || typeof value !== "object") {
            return null;
        }

        const candidate = value as Record<string, unknown>;
        const normalized: QuestionResponseMutationWidgets = {};

        normalized.comments = this.normalizeCommentsWidget(candidate.comments);
        normalized.recentActivity = this.normalizeRecentActivityWidget(candidate.recentActivity);

        normalized.coverSummary = this.normalizeCoverSummary(candidate.coverSummary);

        return normalized;
    }

    private normalizeCommentsWidget(value: unknown): QuestionResponseMutationCommentsWidget | null {
        if (!value || typeof value !== "object") {
            return null;
        }

        const candidate = value as Record<string, unknown>;
        return {
            total: this.normalizeNonNegativeNumber(candidate.total),
            latestCreatedAt: typeof candidate.latestCreatedAt === "string" ? candidate.latestCreatedAt : null,
            latestUpdatedAt: typeof candidate.latestUpdatedAt === "string" ? candidate.latestUpdatedAt : null
        };
    }

    private normalizeRecentActivityWidget(value: unknown): QuestionResponseMutationRecentActivityWidget | null {
        if (!value || typeof value !== "object") {
            return null;
        }

        const candidate = value as Record<string, unknown>;
        return {
            total: this.normalizeNonNegativeNumber(candidate.total),
            latest: this.normalizeRecentActivityWidgetLatest(candidate.latest)
        };
    }

    private normalizeRecentActivityWidgetLatest(
        value: unknown
    ): QuestionResponseMutationRecentActivityWidgetLatest | null {
        if (!value || typeof value !== "object") {
            return null;
        }

        const candidate = value as Record<string, unknown>;
        const idValue = Number(candidate.id);
        return {
            id: Number.isFinite(idValue) && idValue > 0 ? Math.floor(idValue) : null,
            createdAt: typeof candidate.createdAt === "string" ? candidate.createdAt : null,
            action: typeof candidate.action === "string" ? candidate.action : null
        };
    }

    private normalizeCoverSummary(value: unknown): QuestionResponseMutationCoverSummary | null {
        if (!value || typeof value !== "object") {
            return null;
        }

        const candidate = value as Record<string, unknown>;
        const savedRequirement = this.normalizeCoverSummaryRequirement(candidate.savedRequirement);

        return {
            totalRequirements: this.normalizeNonNegativeNumber(candidate.totalRequirements),
            completedRequirements: this.normalizeNonNegativeNumber(candidate.completedRequirements),
            pendingRequirements: this.normalizeNonNegativeNumber(candidate.pendingRequirements),
            completionPercent: this.normalizePercentNumber(candidate.completionPercent),
            savedRequirement
        };
    }

    private normalizeCoverSummaryRequirement(value: unknown): QuestionResponseMutationCoverSummaryRequirement | null {
        if (!value || typeof value !== "object") {
            return null;
        }

        const candidate = value as Record<string, unknown>;
        const section = typeof candidate.section === "string" ? candidate.section.trim() : "";
        const decodedTarget = this.decodeTarget(typeof candidate.target === "string" ? candidate.target : "");
        const target = typeof decodedTarget === "string" ? decodedTarget.trim() : "";

        if (!section || !target) {
            return null;
        }

        return {
            section,
            target,
            complianceStatus:
                candidate.complianceStatus === 1 || candidate.complianceStatus === "1"
                    ? 1
                    : candidate.complianceStatus === 0 || candidate.complianceStatus === "0"
                        ? 0
                        : null,
            qsaReviewed: this.normalizeReviewStatus(candidate.qsaReviewed ?? candidate.qsa_reviewed),
            selectedOption: this.normalizeSelectedOption(candidate.selectedOption),
            respondedAt: typeof candidate.respondedAt === "string" ? candidate.respondedAt : null,
            respondedByName: this.resolveResponderDisplayName(
                typeof candidate.respondedByName === "string" ? candidate.respondedByName : null
            )
        };
    }

    private normalizeNonNegativeNumber(value: unknown): number {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
    }

    private normalizePercentNumber(value: unknown): number {
        const parsed = Number(value);
        if (!Number.isFinite(parsed)) {
            return 0;
        }

        if (parsed <= 0) {
            return 0;
        }

        if (parsed >= 100) {
            return 100;
        }

        return Math.round(parsed * 10) / 10;
    }

    private normalizeAppendixDependencies(
        data: Partial<AppendixDependencyMap> | null | undefined
    ): AppendixDependencyMap {
        const normalized = this.buildEmptyAppendixDependencyMap();
        if (!data || typeof data !== "object") {
            return normalized;
        }

        (Object.keys(normalized) as (keyof AppendixDependencyMap)[]).forEach((key) => {
            normalized[key] = this.normalizeAppendixDependencyEntries(
                (data as Record<string, unknown>)[key]
            );
        });

        return normalized;
    }

    private normalizeAppendixDependencyEntries(value: unknown): AppendixDependencyEntry[] {
        if (!Array.isArray(value)) {
            return [];
        }

        const entries: AppendixDependencyEntry[] = [];

        for (const entry of value) {
            if (!entry || typeof entry !== "object") {
                continue;
            }

            const sectionKey =
                typeof (entry as { sectionKey?: unknown }).sectionKey === "string"
                    ? this.decodeTarget((entry as { sectionKey: string }).sectionKey).trim()
                    : "";
            const questionReference =
                typeof (entry as { questionReference?: unknown }).questionReference === "string"
                    ? (entry as { questionReference: string }).questionReference.trim()
                    : "";

            if (!sectionKey || !questionReference) {
                continue;
            }

            entries.push({ sectionKey, questionReference });
        }

        return entries;
    }

    private buildEmptyAppendixDependencyMap(): AppendixDependencyMap {
        return {
            appendix_b: [],
            appendix_c: [],
            appendix_d: [],
            appendix_e: []
        };
    }

}
